// errors.ts — WHAT THE BOOKS SAID, IN WORDS.
//
// 3 Sept 2026 audit: every screen showed a failed posting as
// `API 400: {"error":"bad_request","reason":"missing required fields"}`. The
// API already knows why it refused; this is the one place that turns its answer
// into a sentence. Screens render `err.message` and never parse JSON.
//
// Rule: name what was refused and what to do next. No codes on screen unless
// the code is the only thing we have.

export interface ApiBody {
  error?: string;
  reason?: string;
  message?: string;
  detail?: string;
  hint?: string;
  field?: string;
  /** The month a close or reopen answer is about ("2026-09"); absent on every other route. */
  period?: string;
  allowed?: unknown[];
  account?: string | null;
  /** The account in the books' own words ("Hot wallet · TRC", a customer's name) — insufficient_funds sends it beside the raw ledger path in `account`. */
  account_name?: string | null;
  violations?: Array<{ account?: string; asset?: string; reason?: string }>;
  legs?: { take?: { asset: string; open: number }; give?: { asset: string; open: number } };
  /** repeat_suspected / money_already_recorded. `mirror` (14 Sept 2026): the
   *  twin is the same trade booked the OTHER WAY ROUND — same party, assets
   *  and amounts, opposite direction (SWEEP-SCOPE §4.3 gap vi). `reason`, when
   *  the BFF sends one, is the sentence the repeat panel shows as it is. */
  twin?: { reference?: string | null; mirror?: boolean; reason?: string };
  /** money_already_recorded from the transfers side (14 Sept 2026, sweep item
   *  19): the twin is a SETTLED TRADE that moved this money itself, not a
   *  standalone movement. The repeat panel words its headline by it. */
  settled?: boolean;
  /** A deal named by a refusal about something else — the trade an entry
   *  settles (the reverse refusals), the trade a movement was drawn by. */
  deal?: string;
  /** new_rail_asset: the assets the rail holds today. */
  holds?: string[];
  current?: string;
  expected?: string;
  needs_confirm?: string;
  coin_legs?: unknown;
  path?: string;
  [k: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: ApiBody;
  constructor(status: number, body: ApiBody | string) {
    const parsed: ApiBody = typeof body === 'string' ? { message: body } : body;
    super(explain(status, parsed));
    this.name = 'ApiError';
    this.status = status;
    this.code = parsed.error ?? `http_${status}`;
    this.body = parsed;
  }
}

const STAGE: Record<string, string> = {
  mc_pending_seller: 'booked, seller not yet paid',
  mc_seller_paid: 'seller paid, not yet delivered',
  settled: 'settled',
  voided: 'voided',
  awaiting: 'awaiting settlement',
  absorbed: 'carried into the opening balances',
};
const stage = (s?: string) => (s && STAGE[s]) || s || 'unknown';

/** The API's own sentence, whichever slot it used. */
function prose(b: ApiBody): string {
  const p = b.reason ?? b.detail ?? b.hint ?? b.message;
  if (typeof p !== 'string' || !p.trim()) return '';
  const t = p.trim();
  // A gateway that answers with an HTML page (Cloudflare 502/524 when the
  // tunnel to the Mac is down) must not be pasted into the operator's sentence.
  if (/^\s*<|<\/?(html|head|body|title)\b/i.test(t)) return '';
  return (t.length > 300 ? `${t.slice(0, 300)}…` : t).replace(/\.$/, '');
}

/** The API's sentence as a sentence: a capital, and the one full stop `prose` took off. */
const sentence = (p: string): string => `${p.charAt(0).toUpperCase()}${p.slice(1)}.`;

/** A back-date the books would not take — `field: 'effective_at'` on a
 *  bad_request or invalid_input (5 Sept 2026, the Loki 3 Sept trade). The
 *  BFF's opening-date refusal is already a sentence and is shown as it is;
 *  its two short reasons get the words the screens use BEFORE sending
 *  (lib/format.ts backdateProblem), so the operator hears one voice whether
 *  the console or the BFF caught it. Only these two codes: period_closed
 *  keeps its own case below, whatever field it names. */
function whenWords(b: ApiBody, p: string): string | null {
  if (b.field !== 'effective_at') return null;
  if (b.error !== 'bad_request' && b.error !== 'invalid_input') return null;
  if (p === 'effective_at is in the future') return 'That time is in the future. Pick the time it actually happened.';
  if (p === 'not a valid ISO instant') return 'That is not a time the books can read. Pick it from the field.';
  return p ? `${p}.` : 'The time given was not accepted. Pick it from the field, or leave it empty for now.';
}

/** A rate the books would not take — `field: 'rate'` on invalid_input (14 Sept
 *  2026, sweep item 21). The BFF refuses "3,67" and "3.67x" BEFORE the deal
 *  insert with the sentence lib/amount.ts rateProblem() shows at the box, so
 *  the operator hears one voice whichever side caught it. */
function rateWords(b: ApiBody, p: string): string | null {
  if (b.error !== 'invalid_input' || b.field !== 'rate') return null;
  return p ? `${p}.` : 'Rate must be a plain number — no commas, no spaces, no letters.';
}

export function explain(status: number, b: ApiBody): string {
  const p = prose(b);
  const when = whenWords(b, p);
  if (when) return when;
  const rate = rateWords(b, p);
  if (rate) return rate;
  switch (b.error) {
    case 'bad_request':
      if (p === 'missing required fields') return 'Something is missing — every amount, party and account has to be filled in before this can post.';
      return p ? `Not accepted: ${p}.` : 'Not accepted — check the fields and try again.';
    case 'invalid_input':
      return p ? `${b.field ? `${b.field}: ` : ''}${p}.` : `${b.field ? `${b.field} is not valid` : 'A field is not valid'}${b.allowed?.length ? ` — allowed: ${b.allowed.join(', ')}` : ''}.`;
    case 'asset_not_allowed_on_rail': {
      const v = b.violations?.[0];
      return v ? `That account cannot hold this asset: ${v.reason ?? `${v.asset} on ${v.account}`}. Pick an account of the right kind.` : 'That account cannot hold this asset. Pick an account of the right kind.';
    }
    case 'chart_violation':
      return `The books refused the account${b.account ? ` ${b.account}` : ''}: ${p || 'it is not in the approved chart'}.`;
    case 'insufficient_funds':
      // The account by NAME when the books send it; the raw ledger path only as the fallback (an older server).
      return b.account_name
        ? `Not enough on ${b.account_name} to post this. Record the money in first, or pick another account.`
        : `Not enough on the source${b.account ? ` (${b.account})` : ''} to post this. Record the money in first, or pick another account.`;
    case 'new_rail_asset':
      return `${p || 'This account has never held this asset'}. If that is really where the money is, confirm the new account-asset pair.`;
    case 'custody_signin':
      return 'Custody sign-in has lapsed.';
    case 'money_already_recorded':
      // Two shapes send this (14 Sept 2026): a SETTLED trade whose leg was
      // already recorded as a movement (trades.ts), and — the mirror, sweep
      // item 19 — a movement whose leg a settled trade already moved
      // (transfers.ts). Both send their own sentence; this is the fallback.
      return p ? `${p}` : 'This money is already in the books — as a movement recorded without a trade, or as a settled trade that moved it itself. Book the trade settle-later so that entry settles it, or confirm it really is a second movement.';
    case 'same_asset':
      // Both sides fold to one asset (sweep item 20). Refused before the deal
      // insert, so nothing landed.
      return p ? `${p}.` : 'Both sides are the same asset — a trade exchanges two different assets. To move money between accounts use Move money.';
    case 'sides_swapped':
      return `${b.fiat ?? 'The currency'} is on the coin side and ${b.coin ?? 'the coin'} on the money side. Flip Customer buys / Customer sells, or swap the two amounts, so the coin is what is traded and the currency is what it is priced in.`;
    case 'repeat_suspected':
      if (b.twin?.mirror) {
        return `The same trade the other way round is already booked${b.twin.reference ? ` as ${b.twin.reference}` : ''}. Check which direction is right before posting it again.`;
      }
      return `This looks like a repeat of an entry already in the books${b.twin?.reference ? ` (${b.twin.reference})` : ''}. Check before posting it again.`;
    // ── REVERSE REFUSALS (sweep item 18, 14 Sept 2026) ─────────────────────
    // A party↔rail movement that a settled trade drew, or that an awaiting
    // trade can still draw, is the settlement of that trade: undoing it
    // leaves the trade paid by nothing. The route names the deal (`deal`) and
    // sends its own sentence; these are the words when it does not.
    case 'drawn_by_deal':
      return p ? `${p}.` : `This entry is the settlement of ${b.deal ?? 'a settled trade'} — reversing it would leave that trade paid by nothing. Void the trade first if the trade is wrong.`;
    case 'settles_open_leg':
      return p ? `${p}.` : `This entry can be the settlement of ${b.deal ?? 'a trade still awaiting'}. Keep it; the sweep uses it once the trade is booked settle-later.`;
    case 'reverse_refused':
      return p ? `${p}.` : 'This entry cannot be reversed — it is, or can be, the settlement of a trade. Keep it, and correct the trade instead.';
    case 'needs_confirm':
      return b.needs_confirm === 'create_mc_seller' ? 'This seller is new — confirm to create them.' : 'This party is new — confirm to create them.';
    case 'legs_open':
      return 'A leg of this trade has neither moved nor netted. Record it with Receive or Pay; the trade closes itself once both legs are settled.';
    case 'not_awaiting':
      return 'Only a settle-later trade still awaiting can be marked settled.';
    case 'not_settled':
      return 'Only a settled trade can be reopened.';
    case 'rate_required':
      return `A rate is needed: ${p || 'a leg of this cheque is in coin, so give the coin-to-AED rate'}.`;
    case 'wrong_lifecycle':
      return `This cheque is ${stage(b.current)}; this step needs it to be ${stage(b.expected)}.`;
    case 'cannot_void': {
      const cur = b.current ?? (b as { current_lifecycle?: string }).current_lifecycle;
      return `This cheque cannot be voided${cur ? ` — it is ${stage(cur)}` : ''}. A settled cheque is corrected with a reversal, not a void.`;
    }
    case 'reason_required':
      // The route says how much it wants (a void asks for ten characters since
      // 14 Sept 2026); its own words win over a fixed number here.
      return b.detail || p || 'Write a reason — it goes into the audit trail.';
    case 'not_last_event':
      return `This trade is not the last event on its cost book, so it cannot be voided on its own. ${p || 'Void the later trades first, or use Rebuild.'}`;
    case 'book_not_unwound':
      return `The cost book could not be unwound: ${p || 'another event sits on it'}. Nothing was changed.`;
    case 'no_basis':
      return `The cost book has no opening basis for this asset: ${p || 'set the opening basis in Inventory first'}.`;
    case 'invalid_quantity': case 'invalid_amount': case 'incoherent_book':
      return `The cost book refused this: ${p || b.error}.`;
    case 'period_closed':
      return `${p || 'That month is closed'}. Reopen the month in Close before posting into it.`;
    case 'merchant_not_found':
      return `${p || 'The merchant must already exist'}. Create them in Parties first.`;
    case 'unknown_expense_category':
      return `That cost category does not exist${b.allowed?.length ? ` — use one of: ${b.allowed.join(', ')}` : ''}.`;
    case 'party_source_not_allowed': case 'party_destination_not_allowed':
      return 'Money to or from a party is recorded with the party named on the other side — pick the party, not a bare receipt or payment.';
    case 'reference_taken':
      return 'This reference is already posted — the entry exists, it was not posted twice.';
    case 'ambiguous_reference':
      return 'More than one posting carries this reference — name the transaction instead.';
    case 'already_voided':
      return 'This trade is already voided.';
    case 'wrong_route':
      return "A manager's cheque is voided from its own screen.";
    case 'no_postings_found':
      return 'No live posting was found for this trade, so there is nothing to reverse.';
    case 'conflict': case 'duplicate':
      return `${p || 'This already exists'}.`;
    case 'not_found':
      return `Not found${b.path ? ` (${b.path})` : ''}. It may have been removed, or the link is stale — refresh and try again.`;
    case 'forbidden':
      return `You are not allowed to do this${p ? `: ${p}` : ''}.`;
    case 'unauthorized':
      return 'Your sign-in has expired. Sign in again.';
    case 'chain_swap_retired':
      return 'Chain swaps are retired in these books — the wallets carry one asset each; move money between wallets instead.';
    case 'cannot_demote':
      return 'A tracked party cannot be turned back into treasury-only.';
    case 'empty_patch':
      return 'Nothing to change.';
    case 'bad_period':
      return 'Give the month as YYYY-MM.';
    case 'period_not_ended':
      return 'That month has not ended yet — it can be closed from the first of next month.';
    case 'later_period_closed':
      return 'A later month is already closed. Reopen the later month first.';
    case 'not_closed':
      return 'That month is not closed.';
    case 'energy_not_wired':
      // The energy desk is a separate service; until the Books are given its
      // address they have nothing to show. Not a fault, and nothing was ordered.
      return 'The energy desk is not connected to the Books yet.';
    case 'energy_unreachable':
      // The route's own sentence says which it was — the desk asleep, slow, or
      // refusing the Books' key — and whether the desk may have acted. Never
      // the generic "the books did not answer": the books are fine.
      return p ? `${p.charAt(0).toUpperCase()}${p.slice(1).replace(/\.$/, '')}.` : 'The energy desk did not answer.';
    case 'guard_unread':
      // A repeat guard that could not READ the books (503, 26 Oct 2026: Move money, Record charge, Between parties). The books DID answer — they refused, before any posting — so the generic
      // 503 sentence below ("the desk Mac may be asleep") is wrong, and the route's own sentence says what to do. Words only: this is NOT a pre-post refusal in nothingLanded's sense — it is a
      // transient failure of the check, and says nothing about an EARLIER try whose answer was lost; the form keeps its key and the retry, the same request, dedupes if that try landed.
      return p ? sentence(p) : 'Could not check whether this money is already in the books — nothing was posted. Try again in a moment.';
    case 'chain_transfer_busy':
      // Another booking of the SAME chain transfer was being posted (503, 27 Oct 2026, Mechanisms' lib/chain-lock.ts CHAIN_BUSY): this door waited for the transfer's lock and gave up before its
      // check — nothing was posted, and a moment later the check sees the other booking. The books answered, so the generic 503 sentence ("the desk Mac may be asleep") is wrong. Words only,
      // as guard_unread: the form keeps its key, and the retry is the same request.
      return p ? sentence(p) : 'Another booking of this chain transfer is being posted — nothing was posted. Try again in a moment.';
    // Services the Books call out to (the screening worker, the onboarding worker) are a different cause from "the books did not answer": the generic 502/503 sentence below would send the
    // operator to the Mac. The route's own sentence names what failed ("the screening worker answered 401 — its admin key may have rotated, or the worker is down"); "not wired" is deployment
    // wording (a service binding, an admin key) the operator cannot act on, so those say only that the service is not connected. (Error-code pass, 26 Oct 2026.)
    case 'kyt_not_wired':
      return 'The screening service is not connected to the Books yet.';
    case 'leads_not_wired':
      return 'The onboarding service is not connected to the Books yet.';
    case 'books_not_wired':
      return 'The Books are not connected to their database on this server.';
    case 'kyt_unreachable':
      return p ? sentence(p) : 'The screening service did not answer.';
    case 'leads_unreachable':
      return p ? sentence(p) : 'The onboarding service did not answer.';
    case 'owner_only':
      // 403: the generic "you are not allowed to do this" below hid the route's own words (WalletRegister's comment promised them: "the server says so in words").
      return p ? sentence(p) : 'Only the owner can do this.';
    case 'ledger_unreachable': case 'formance_error':
      return 'The ledger did not answer. The books on the Mac may be down — check Docker is running, then try again.';
    case 'db_error':
      return 'The desk database did not answer. Try again in a moment; if it keeps failing, the tunnel to the Mac may be down.';
    case 'ledger_revert_failed': case 'revert_failed':
      // A month's REOPEN answers revert_failed too, with a full sentence of its own — what was undone and
      // to reopen again. The trade-void tail below ("nothing else was changed — check Trades") would
      // contradict it and point at the wrong screen, so the month's answer is printed alone.
      if (b.period && p) return `${p}.`;
      return `The reversal did not complete: ${p || 'the ledger refused'}. Nothing else was changed — check Trades before retrying.`;
    case 'internal_error':
      // NEVER "nothing was posted": trades, promote and the inventory repairs
      // post ledger legs BEFORE their database commit, so a 500 can arrive with
      // money already moved (3 Sept audit). Telling the operator nothing landed
      // invites a second posting of the same trade.
      return `The books hit a fault: ${p || 'no detail'}. Check the trades log before posting this again.`;
    case 'ledger_move_failed':
      return `Part of this move posted and part did not: ${p || 'the ledger refused partway'}. Nothing was relabelled, and the same request can be sent again safely.`;
    case 'rekey_failed': case 'rebuild_failed':
      return `The cost-book repair stopped partway: ${p || 'the ledger refused'}. Check Inventory before running it again.`;
    case 'gate_failed':
      return `The month cannot close yet — a gate did not pass${p ? `: ${p}` : ''}. Open Close to see which one.`;
    // The month close's three 502s (cloud audit M1/M2, 5 Oct 2026). The server's own sentence says what
    // happened; the generic 502 below says "Nothing reached the ledger", which is wrong for all three, so
    // each fallback claims only what its code means — and the unknown outcome never claims nothing happened.
    case 'close_failed':
      return p ? `${p}.` : 'The close stopped partway. Nothing was sealed and the month is still open — open Close to see where it stands.';
    case 'close_blocked':
      return p ? `${p}.` : 'An earlier close attempt could not be undone just now, so nothing was posted. The month is still open — open Close to see where it stands.';
    case 'close_outcome_unknown':
      return p ? `${p}.` : 'The connection dropped while the close was being saved, so it may or may not have gone through. Open Close to see whether the month is sealed.';
    case 'out_of_order':
      return 'Close the earlier month first — months close in order.';
    case 'already_closed':
      return 'That month is already closed. Reopen it if you need to post into it.';
    case 'party_not_found':
      return 'No party by that name. Check the spelling, or create them first.';
    case 'unknown_party':
      // Record charge and the profit share send this as a 404 with the slug and the desk in the sentence; the canned 404 line ("the link may be stale") hid it.
      return p ? sentence(p) : 'No party by that name on this desk. Pick them from the list.';
    case 'not_a_counterparty':
      return 'This only applies to a merchant. This party is on the customer side.';
    case 'parent_not_found':
      return 'The parent party does not exist. Create it first, or leave the parent empty.';
    case 'already_a_merchant':
      return 'This party is already a merchant.';
    case 'not_promotable':
      return `This party cannot be turned into a merchant${p ? `: ${p}` : ''}.`;
    case 'bad_id': case 'not_a_deal_id':
      return 'That reference is not one the books recognise.';
    case 'bad_action':
      return 'That action is not one this screen can take.';
    case 'world_not_intended':
      return `This posting would create money out of nowhere: ${p || 'no party on the other side'}. Name the party it came from or went to.`;
    case 'new_rail_asset_confirm':
      return `${p || 'This account has never held this asset'}.`;
  }
  if (status === 404) return 'Not found — the link may be stale. Refresh and try again.';
  if (status === 401) return 'Your sign-in has expired. Sign in again.';
  if (status === 403) return 'You are not allowed to do this.';
  if (status === 502 || status === 503 || status === 504) {
    return 'The books did not answer. The desk Mac may be asleep or Docker may not be running. Nothing reached the ledger — try again once it is up.';
  }
  if (status >= 500) return `The books hit a fault${p ? `: ${p}` : ''}. Check the trades log before posting this again.`;
  if (p) return sentence(p);
  return b.error ? `Refused (${b.error}).` : `Refused (HTTP ${status}).`;
}

/** THE BOOKS DID NOT ANSWER IN TIME — a request cut at its bound (lib/api.ts REQUEST_LIMITS), so a screen never waits for ever on "Reading…" or
 *  "Posting…". A READ posted nothing: it failed, and says so with its way out. Only a call that opts in to a bound for a CHANGE (`timeoutMs`) can raise
 *  `wrote`: a change — a trade, a label, an approval, a party — may have reached the books before the wait ran out, so it says that and says to check (not
 *  WHERE: a trade is in the trades log, a label is in the register). No change is bounded by default (lib/api.ts REQUEST_LIMITS). */
export class NoAnswer extends Error {
  /** true: it was a change, and may have been recorded. */
  readonly wrote: boolean;
  constructor(wrote: boolean) {
    super(wrote
      ? 'The books did not answer in time — it may have been recorded. Check before sending it again.'
      : 'The books did not answer in time. Try again.');
    this.name = 'NoAnswer';
    this.wrote = wrote;
  }
}

/** The one retry a read gets (a dropped tunnel packet) — and none when it already waited out its whole bound: the second wait would only double
 *  the time before the line says it failed. */
export const retryRead = (failures: number, e: unknown): boolean => failures < 1 && !(e instanceof NoAnswer) && !isCustodySignin(e);

/** THE CUSTODY SIGN-IN HAS LAPSED (his 26 Oct Face ID rule: Cloudflare Access in front of the custody API asks again every
 *  24 h). Every /api/custody read then meets Access's redirect, which a fetch cannot follow — lib/api.ts request() turns it
 *  into this one error, and the custody screens say it ONCE ("Sign in to custody"), never a red line per read (his "why it
 *  all goes red everytime", 2 Nov 2026). Asking again does not help, so it is not retried. */
export function isCustodySignin(e: unknown): boolean {
  return e instanceof ApiError && e.code === 'custody_signin';
}

/** Words for anything a mutation or query threw. */
export function errorWords(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof TypeError && /fetch|network/i.test(e.message)) return 'No connection — the request never reached the books. Check the network and try again.';
  const m = (e as Error)?.message ?? String(e);
  return m || 'Something went wrong.';
}

/** THE SENTENCE FOR A READ THAT FAILED. errorWords answers a button press: a 5xx or a gateway page MAY HAVE PARTLY POSTED, so its
 *  generic words end "Nothing reached the ledger" / "Check the trades log before posting this again". A read posts nothing —
 *  those clauses would send the operator looking for a booking nobody attempted. Everything else is errorWords' own sentence
 *  (a named refusal, the desk-database and ledger lines, the connection line). */
export function readWords(e: unknown): string {
  const w = errorWords(e);
  if (!(e instanceof ApiError)) return w;
  return w
    .replace(' Nothing reached the ledger — try again once it is up.', ' Try again once it is up.')
    .replace(' Check the trades log before posting this again.', '');
}

/** OUR OWN MARKERS, not refusals. NewTrade / RecordMoney / ManagerCheque throw
 *  `new Error('needs_confirm' | 'repeat_suspected')` to steer the form into a
 *  confirm panel; the screen must not also print them as an error sentence. */
const SENTINELS = new Set(['needs_confirm', 'repeat_suspected']);
export function isSentinel(e: unknown): boolean {
  return SENTINELS.has((e as Error)?.message ?? '');
}

/** DID THE REFUSAL COME BEFORE THE FIRST LEDGER WRITE?
 *
 *  A posting's idempotency key (the deal id, the transfer reference) may be
 *  re-minted only when the books provably hold nothing under it: a retry under
 *  the same key dedupes, a fresh key books the same money twice. "Any status
 *  under 500" was taken as that proof and is not (review, 5 Sept 2026):
 *  bff/src/routes/trades.ts posts the delivery legs (`:in`, `:out`), then the
 *  trade, then the `:margin` memo, and a throw between them reaches refusal()
 *  as chart_violation 400, an InventoryError 409 or insufficient_funds 409
 *  with legs already in Formance — which is append-only, so they stay.
 *
 *  Only codes every posting route answers BEFORE its first write are listed:
 *  bad_request, invalid_input and asset_not_allowed_on_rail are returned by
 *  trades.ts (~596-716) and transfers.ts (~61-76) ahead of their `try`;
 *  period_closed comes from assertPeriodOpen(), which trades.ts asks before
 *  the deal row is inserted and submitScript() asks before the ledger is
 *  touched. Our own sentinels are panels, not postings. Everything else —
 *  a 5xx, a timeout, a lost response, and any 4xx not named here — keeps
 *  the key. same_asset (14 Sept 2026) joins them: trades.ts answers it in
 *  the shape checks, ahead of its `try`, beside the rate's invalid_input. */
const PRE_POST_REFUSALS = new Set(['bad_request', 'invalid_input', 'asset_not_allowed_on_rail', 'period_closed', 'same_asset', 'new_rail_asset']);
export function nothingLanded(e: unknown): boolean {
  return isSentinel(e) || (e instanceof ApiError && PRE_POST_REFUSALS.has(e.code));
}

